import { useTranslation } from '@paintscout/ui'
import { DialogStackContext, useClientOptions, useUser, useUserPermissions, useUsers } from '@paintscout/ui-business'
import {
  expiryEnabled,
  formatPhoneNumber,
  getExpiryDate,
  getJobAddress,
  getJobIdentifier,
  getObjectLabels,
  getQuoteTypeOptions,
  setEstimator
} from '@paintscout/util/builder'
import type { Auth0User } from 'paintscout'
import type { Contact, User } from '@paintscout/api'
import addressFormat from 'address-format'
import find from 'lodash/find'
import get from 'lodash/get'
import moment from 'moment'
import { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { canBeEstimator, removeAuth0Prefix } from '@paintscout/util/users'
import { useQuote } from '../../context'
import { ChangeEstimatorDialog } from '../../dialogs'

/** The contact fields the header renders — shared by `quote.contact` and `quote.secondaryContact`. */
type HeaderContact = Partial<
  Pick<
    Contact,
    | 'first_name'
    | 'last_name'
    | 'company'
    | 'address'
    | 'city'
    | 'province'
    | 'postal'
    | 'email'
    | 'alternate_email'
    | 'phone_number'
    | 'phone_ext'
    | 'alternate_phone'
    | 'alternate_phone_ext'
  >
>

export interface UseQuoteDocumentHeaderDataArgs {
  /** `work-order` drops the contact columns and shows one plain document date. */
  variant: 'estimate' | 'work-order'
}

/** Everything the two document-header layouts render, derived once. */
export type QuoteDocumentHeaderData = ReturnType<typeof useQuoteDocumentHeaderData>

/**
 * All of the document header's data and editing handlers, in one hook so the desktop and mobile layouts
 * stay pure markup. Reads the same sources the legacy QuoteHeader cards do: company options plus the
 * quote-type company override, quote dates/expiry, contact, job address, and the owner (estimator).
 *
 * The two anchor states live here rather than in a layout because the contact menu and the date/expiry
 * pop-over are rendered by the shared parent — a breakpoint change mid-session swaps the layout, and an
 * open menu must survive it.
 */
export function useQuoteDocumentHeaderData({ variant }: UseQuoteDocumentHeaderDataArgs) {
  const { __ } = useTranslation()
  const {
    quote,
    isEditable,
    isContactEditable,
    onEditContact,
    onSelectContact,
    onViewContact,
    onViewCompany,
    onSelectSecondaryContact,
    onEditSecondaryContact,
    onRemoveSecondaryContact,
    onEditJobAddress,
    updateQuote
  } = useQuote()
  const { options, clientId } = useClientOptions()
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const { isSuperadmin } = useUser()
  const { userPictures, users } = useUsers()

  const { permissionToViewEstimator, permissionToCreateContact } = useUserPermissions()
  const currentEstimatorId = useRef<string>(quote?.owner?.id ?? '')

  // The "Prepared for" card opens a contact menu (Edit / Swap or Create / Add Secondary + tel/mailto),
  // mirroring the classic ContactCard menu rather than jumping straight into the edit dialog.
  const [contactAnchorEl, setContactAnchorEl] = useState<HTMLElement | null>(null)
  // The "Secondary Contact" card opens its own menu (Edit / Swap or Create / Remove + tel/mailto),
  // mirroring the classic SecondaryContactCard.
  const [secondaryContactAnchorEl, setSecondaryContactAnchorEl] = useState<HTMLElement | null>(null)
  // The date/expiry chip opens a pop-over hosting the classic QuoteDateCard editor.
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null)

  // Sales Rep card → the estimator picker (mirrors the classic EstimatorCard, which opens this dialog
  // directly rather than via a context handler).
  const onSelectEstimator = useCallback(() => {
    return openDialog(ChangeEstimatorDialog, {
      queryOverride: isSuperadmin ? `app_metadata.companyId:"${clientId}" AND app_metadata.active:true` : '',
      currentEstimatorId: currentEstimatorId.current,
      onConfirm: (user: User) => {
        const estimatorProps = user
          ? { quote, user: user as Auth0User, signature: user.signature }
          : { quote, user: null }
        currentEstimatorId.current = user?.user_id ?? ''
        updateQuote({ quote: setEstimator(estimatorProps) })
        dismissDialog()
      },
      onCancel: dismissDialog
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, dismissDialog, isSuperadmin, clientId, quote, updateQuote])

  // The phone sheet's estimator dropdown (ENG-2080): the users the Change Estimator dialog offers —
  // active, able to own a document, and (for a superadmin, whose list spans accounts) on this account.
  const estimatorOptions = useMemo(
    () =>
      (users ?? []).filter(
        (user) =>
          user.app_metadata?.active &&
          canBeEstimator(user) &&
          (!isSuperadmin || user.app_metadata?.companyId === clientId)
      ),
    [users, isSuperadmin, clientId]
  )

  // Set the estimator straight from that dropdown, the same write the dialog's Done makes.
  const setEstimatorUser = (user: User) => {
    currentEstimatorId.current = user.user_id
    updateQuote({ quote: setEstimator({ quote, user: user as Auth0User, signature: user.signature }) })
  }

  const forceIntegrationContacts = !!options.options.quotes?.forceIntegrationContacts
  // Contact editing has its own permission scope (contact-write users who can't otherwise edit the
  // document), same fallback the classic ContactCard/SecondaryContactCard use.
  const contactEditable = isContactEditable ?? isEditable
  const contactClickable = contactEditable && (!!onSelectContact || !!onEditContact)
  const canCreateContact = permissionToCreateContact && !forceIntegrationContacts
  const canAddSecondaryContact =
    !!onSelectSecondaryContact &&
    !quote?.secondaryContact &&
    !!options.options.features?.quotes?.secondaryContact?.enabled &&
    !forceIntegrationContacts
  const jobAddressClickable = isEditable && !!onEditJobAddress
  const estimatorClickable = isEditable && permissionToViewEstimator

  const co = options.options
  const quoteType = find(getQuoteTypeOptions({ options }), { key: quote?.int_ext?.value as any })
  const override = quoteType?.overrideCompanyOptions

  const companyName = String((override ? quoteType?.companyOptions?.name : co.companyName?.value) ?? '')
  const logo = override ? get(quoteType, 'companyOptions.logo.value', '') : co.logo?.value
  const companyAddress = addressFormat({
    address: (override ? quoteType?.companyOptions?.address : co.companyAddress?.value) ?? '',
    city: (override ? quoteType?.companyOptions?.city : co.companyCity?.value) ?? '',
    subdivision: (override ? quoteType?.companyOptions?.province : co.companyProv?.value) ?? '',
    postalCode: (override ? quoteType?.companyOptions?.postal : co.companyPostal?.value) ?? '',
    countryCode: 'US'
  })
  const companyPhoneRaw = override ? quoteType?.companyOptions?.phoneNumber : co.companyPhone?.value
  const companyPhone = companyPhoneRaw ? formatPhoneNumber({ options, phoneNumber: companyPhoneRaw }) : null
  // "Additional Company Info" (Settings → Company). Free-form text — license numbers, tag lines, etc.
  const companyAdditional = String(
    (override ? quoteType?.companyOptions?.additional : co.companyAdditional?.value) ?? ''
  ).trim()

  const isWorkOrder = variant === 'work-order'
  const objectLabels = getObjectLabels({ options, invoice: quote?.is_invoice })
  // Through the dictionary so the header title follows the document's language, the same way the
  // classic QuoteStatusCard does it (ENG-1497). A default label ("Work Order", "Estimate") has an
  // entry; an account's custom label has none and falls through unchanged.
  const docLabel = __(isWorkOrder ? objectLabels.workOrder.value : objectLabels.quote.value)
  const quoteId = quote?.number && quote.number !== -1 ? quote.number : null

  const issued = quote?.quote_date ? moment(quote.quote_date).format('MMM D') : null
  const expiryDate = expiryEnabled({ quote, options }) ? getExpiryDate({ quote }) : null
  const expires = expiryDate ? moment(expiryDate).format('MMM D') : null
  // The Work Order header shows a single, plain document date (not the clickable Issued/Expires chip).
  const workOrderDate = quote?.quote_date ? moment(quote.quote_date).format('MMMM D, YYYY') : null
  // The mobile summary row's date: one plain document date (no Issued/Expires split — that detail lives
  // in the expanded header). Still opens the same date/expiry editor on an estimate.
  // A work order reads "Issued: Dec 19", as the prototype's work-order summary row does (ENG-2080).
  const collapsedDate = quote?.quote_date
    ? isWorkOrder
      ? `${__('Issued')}: ${moment(quote.quote_date).format('MMM D')}`
      : moment(quote.quote_date).format('MMM D, YYYY')
    : null
  // The same date spelled out in the expanded company group (mirrors the desktop meta row's placement).
  const expandedDate = quote?.quote_date ? moment(quote.quote_date).format('MMMM D, YYYY') : null

  // Every detail line the classic ContactCard rendered — company, mailing address, and both emails and
  // both phone numbers. The redesigned header used to show name / email / phone only, which dropped the
  // company, the address, and the alternate contact details.
  //
  // `emails` and `phones` are lists rather than a primary + fallback pair: a contact reachable at two
  // numbers should show both, and a contact with only an alternate still shows that one. Deduped, so a
  // value repeated in both fields is only rendered once.
  const getContactDetails = (subject?: HeaderContact) => {
    if (!subject) {
      return { name: null, company: null, addressLines: [] as string[], emails: [] as string[], phones: [] as string[] }
    }
    const addressLines: string[] =
      subject.address || subject.city || subject.province || subject.postal
        ? addressFormat({
            address: subject.address ?? '',
            city: subject.city ?? '',
            subdivision: subject.province ?? '',
            postalCode: subject.postal ?? '',
            countryCode: 'US'
          })
        : []
    const phoneNumber = subject.phone_number
      ? formatPhoneNumber({ options, phoneNumber: subject.phone_number, extension: subject.phone_ext })
      : null
    const alternatePhone = subject.alternate_phone
      ? formatPhoneNumber({ options, phoneNumber: subject.alternate_phone, extension: subject.alternate_phone_ext })
      : null
    return {
      name: [subject.first_name, subject.last_name].filter(Boolean).join(' ') || null,
      company: subject.company ?? null,
      addressLines,
      emails: [...new Set([subject.email, subject.alternate_email].filter(Boolean))],
      phones: [...new Set([phoneNumber, alternatePhone].filter(Boolean))]
    }
  }

  const contact = quote?.contact
  const contactDetails = getContactDetails(contact)
  const contactName = contactDetails.name
  const contactCompany = contactDetails.company
  // Only a company linked to a Company record has a profile to open; a free-text one stays plain.
  const onViewContactCompany = contact?.companyDocId && onViewCompany ? onViewCompany : undefined
  const contactPhones = contactDetails.phones
  const contactEmails = contactDetails.emails

  // The secondary contact only renders once one has been added — there is no "add" placeholder column
  // for it (the primary contact's menu is where one gets added).
  const secondaryContact = quote?.secondaryContact
  const secondaryDetails = getContactDetails(secondaryContact)
  const secondaryContactName = secondaryDetails.name
  const secondaryContactCompany = secondaryDetails.company
  const secondaryContactPhones = secondaryDetails.phones
  const secondaryContactEmails = secondaryDetails.emails
  const hasSecondaryContact = !!secondaryContact
  // Edit/swap/remove all hang off the same contact-write scope; without it the column is display-only.
  const secondaryContactClickable =
    hasSecondaryContact && contactEditable && (!!onEditSecondaryContact || !!onSelectSecondaryContact)

  // "Prepared for" activation: with no contact yet there's nothing to edit/swap, so jump straight to
  // the contact search dialog; once a contact exists, open the menu (Edit / Swap / Add Secondary / …).
  const activateContact = (anchor: HTMLElement) => {
    if (contact) {
      setContactAnchorEl(anchor)
    } else {
      onSelectContact?.()
    }
  }

  const activateSecondaryContact = (anchor: HTMLElement) => setSecondaryContactAnchorEl(anchor)

  const jobIdentifier = getJobIdentifier({ quote })
  const jobAddress = getJobAddress({ quote })
  const jobAddressLines: string[] =
    jobAddress && (jobAddress.address || jobAddress.city || jobAddress.province || jobAddress.postal)
      ? addressFormat({
          address: jobAddress.address ?? '',
          city: jobAddress.city ?? '',
          subdivision: jobAddress.province ?? '',
          postalCode: jobAddress.postal ?? '',
          countryCode: 'US'
        })
      : []
  const hasJobAddress = !!jobIdentifier || jobAddressLines.length > 0

  // A contact's mailing address is usually the job address too, and the Job Address column sits right
  // beside this one — so only show it when the two differ.
  const contactAddressLines =
    contactDetails.addressLines.join(', ') === jobAddressLines.join(', ') ? [] : contactDetails.addressLines
  const secondaryContactAddressLines =
    secondaryDetails.addressLines.join(', ') === jobAddressLines.join(', ') ? [] : secondaryDetails.addressLines

  const owner = quote?.owner
  const estimatorName = owner ? [owner.firstName, owner.lastName].filter(Boolean).join(' ') : null
  const estimatorEmail = owner?.email ?? null
  const estimatorPhone = owner?.phoneNumber
    ? formatPhoneNumber({ options, phoneNumber: owner.phoneNumber, extension: owner.phoneExtension })
    : null
  const ownerUser = (users ?? []).find((u) => u.user_id.replace(/^auth0\|/, '') === owner?.id)

  return {
    /**
     * A template has no customer: its Prepared for / Job Address / Prepared by columns are filled in
     * on the estimates made from it, not here. The layouts render one inactive placeholder per column
     * in that case, rather than the "Add Contact" affordances of a real estimate (ENG-1282).
     */
    isTemplate: quote?.type === 'quoteTemplate',

    // context handlers the layouts call directly
    isEditable,
    onEditContact,
    onSelectContact,
    onViewContact,
    onSelectSecondaryContact,
    onEditSecondaryContact,
    onRemoveSecondaryContact,
    onEditJobAddress,
    onSelectEstimator,
    setEstimatorUser,

    // company block
    companyName,
    logo,
    companyAddress,
    companyPhone,
    companyAdditional,

    // document label / dates
    isWorkOrder,
    docLabel,
    quoteId,
    issued,
    expires,
    workOrderDate,
    collapsedDate,
    expandedDate,

    // "Prepared for"
    contact,
    contactName,
    contactCompany,
    onViewContactCompany,
    contactAddressLines,
    contactPhones,
    contactEmails,
    contactClickable,
    canCreateContact,
    canAddSecondaryContact,
    activateContact,

    // "Secondary Contact"
    secondaryContact,
    secondaryContactName,
    secondaryContactCompany,
    secondaryContactAddressLines,
    secondaryContactPhones,
    secondaryContactEmails,
    secondaryContactClickable,
    hasSecondaryContact,
    activateSecondaryContact,
    // Whether each region has a value. Derived once because it drives two things that must agree: the
    // dashed placeholder in the column body and the muted tick beside its label.
    hasContact: !!contact,

    // Job Address
    jobIdentifier,
    jobAddressLines,
    jobAddressClickable,
    hasJobAddress,
    // An empty Job Address region is only worth showing when the viewer can fill it. Read-only viewers
    // (the customer view) would otherwise see a bare "—" column (ENG-2037).
    showJobAddress: hasJobAddress || jobAddressClickable,

    // "Prepared by"
    owner,
    ownerUser,
    userPictures,
    estimatorName,
    estimatorEmail,
    estimatorPhone,
    estimatorClickable,
    estimatorOptions,
    // Whether a user is the current estimator, ignoring the auth0| prefix the two ids differ by.
    isCurrentEstimator: (user: User) => !!owner && removeAuth0Prefix(user.user_id) === removeAuth0Prefix(owner.id),
    hasEstimator: !!owner,
    // Hidden when there is no estimator and the viewer cannot assign one — e.g. the customer view of an
    // estimate with no owner (ENG-2209), the same rule as `showJobAddress`.
    showEstimator: !!owner || estimatorClickable,

    // pop-over / menu anchors, owned here so they outlive a layout swap
    contactAnchorEl,
    setContactAnchorEl,
    secondaryContactAnchorEl,
    setSecondaryContactAnchorEl,
    dateAnchorEl,
    setDateAnchorEl
  }
}

export default useQuoteDocumentHeaderData
